import { useAtom } from "jotai";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useWorkspacePermissions from "../../../../hooks/useWorkspacePermissions";
import { workspaceIdAtom } from "../../../../atoms/WorkspaceAtoms";
import PageAPI from "../../../api/PageAPI";
import { getErrorMessage } from "../../../../utils/GeneralUtils";
import { newToastEvent } from "../../../../utils/ToastUtil";
import WorkspaceAPI from "../../../api/WorkspaceAPI";

// This react query hook is used to update a workspace's sidebar items.
const useUpdateItem = () => {
  const queryClient = useQueryClient();
  const { wPermissions } = useWorkspacePermissions();
  const [workspaceID] = useAtom(workspaceIdAtom);

  const handleIsPageLink = async (item: any, newName: string) => {
    if (!item?.PageID) return;

    const data = await PageAPI.updatePageSettings(workspaceID, item?.PageID, {
      Name: newName,
    });

    queryClient.setQueryData(["page", item?.PageID], data.data.data.page);
    queryClient.invalidateQueries({
      queryKey: ["pages", workspaceID],
    });
  };

  const updateItem = useMutation({
    mutationFn: ({ newName, itemId }: { newName: string; itemId: string }) => {
      if (!wPermissions.workspace_modify_workspace) {
        return Promise.reject(
          "You do not have permission to modify this workspace"
        );
      }

      const { workspace } = queryClient.getQueryData<any>([
        "workspace",
        workspaceID,
      ]);

      const newSidebar = workspace.Sidebar.map((item: any) => {
        if (item.id === itemId) {
          handleIsPageLink(item, newName);

          return {
            ...item,
            Title: newName,
          };
        }

        if (item.Type === "Group") {
          item.Items.map((subItem: any) => {
            if (subItem.id === itemId) {
              handleIsPageLink(subItem, newName);

              return {
                ...subItem,
                Title: newName,
              };
            }

            return subItem;
          });
        }

        return item;
      });

      return WorkspaceAPI.updateWorkspace(workspaceID, {
        Sidebar: newSidebar,
      }).then((data) => {
        const updatedWorkspace = data.data.data.workspace;

        queryClient.setQueryData<any>(
          ["workspace", workspaceID],
          (data: any) => ({
            ...data,
            workspace: updatedWorkspace,
          })
        );
      });
    },
    onError: (error) => {
      console.error(getErrorMessage(error));
      newToastEvent("error", getErrorMessage(error));
    },
  });

  return { updateItem: updateItem.mutate };
};

export default useUpdateItem;
